/**
 * GLSL for the wind layer (`WindLayer.ts`): a world losing its atmosphere to the solar wind.
 *
 *  - `WIND_FRAGMENT`, a full-screen pass into a low-resolution target: an analytic exosphere hugging
 *    the limb (pressed thin on the dayside, blown out downwind, ragged), and a raymarched plume that
 *    peels off the terminator ring and streams into a long, slightly curving tail.
 *  - `COMPOSITE_FRAGMENT` adds that target over the scene through a small tent filter.
 *  - `STREAK_VERTEX` places line segments carried downwind along the tail's bend.
 *  - `NOISE_BAKE_FRAGMENT` fills the pass's tileable 3D noise texture once, one layer per draw.
 *
 * World space is the planet's frame (radius 1 at the origin, the camera unrotated on +z), so the
 * light and the wind are fixed in it and the tail never turns with the surface. Every animated term
 * reads `uTime`, the stage's shared clock.
 */

/**
 * Tileable value-noise fbm for one z-layer of a size³ texture: octaves of 4, 8, 16 and 32 cells
 * across the tile, so every octave wraps.
 */
export const NOISE_BAKE_FRAGMENT = /* glsl */ `
uniform float uLayer;
uniform float uSize;
float lattice(vec3 c, float n, float octave) {
  c = mod(c, n);
  return fract(sin(dot(c, vec3(12.9898, 78.233, 37.719)) + octave * 17.17) * 43758.5453);
}
float valueNoise(vec3 p, float n, float octave) {
  vec3 i = floor(p);
  vec3 f = p - i;
  f = f * f * (3.0 - 2.0 * f);
  float c000 = lattice(i, n, octave), c100 = lattice(i + vec3(1, 0, 0), n, octave);
  float c010 = lattice(i + vec3(0, 1, 0), n, octave), c110 = lattice(i + vec3(1, 1, 0), n, octave);
  float c001 = lattice(i + vec3(0, 0, 1), n, octave), c101 = lattice(i + vec3(1, 0, 1), n, octave);
  float c011 = lattice(i + vec3(0, 1, 1), n, octave), c111 = lattice(i + vec3(1, 1, 1), n, octave);
  return mix(mix(mix(c000, c100, f.x), mix(c010, c110, f.x), f.y), mix(mix(c001, c101, f.x), mix(c011, c111, f.x), f.y), f.z);
}
void main() {
  vec3 p = vec3(gl_FragCoord.xy - 0.5, uLayer) / uSize;
  float v = 0.5 * valueNoise(p * 4.0, 4.0, 0.0) + 0.28 * valueNoise(p * 8.0, 8.0, 1.0)
    + 0.15 * valueNoise(p * 16.0, 16.0, 2.0) + 0.07 * valueNoise(p * 32.0, 32.0, 3.0);
  gl_FragColor = vec4(v, v, v, 1.0);
}
`;

/** The stripped atmosphere: the exosphere at the limb and the plume. */
export const WIND_FRAGMENT = /* glsl */ `
precision highp sampler3D;
uniform mat4 uInvProj;
uniform mat4 uCamWorld;
uniform vec3 uCamPos;
uniform vec3 uLight;
uniform vec3 uWind;
uniform vec3 uWindU;
uniform vec3 uWindV;
uniform float uTime;
uniform float uBend;
uniform float uLength;
uniform float uFlowSpeed;
uniform float uLimb;
uniform float uTail;
uniform vec3 uLimbColor;
uniform vec3 uCoreColor;
uniform vec3 uTailColor;
uniform sampler3D uNoise;
varying vec2 vUv;

float n3(vec3 p) { return texture(uNoise, p).r; }

float hash(vec2 px) { return fract(sin(dot(px, vec2(12.9898, 78.233))) * 43758.5453); }

/** Lit unless inside the planet's shadow cylinder (soft-edged). */
float sunlit(vec3 p) {
  float along = dot(p, uLight);
  float perp = length(p - along * uLight);
  // The shadow deepens over the first stretch behind the terminator plane, so there is no seam there.
  return mix(1.0, smoothstep(0.88, 1.12, perp), smoothstep(0.0, 0.5, -along));
}

/** The plume: gas torn off the limb, streaming downwind and flaring into a curving tail. */
float plume(vec3 p, out float ap) {
  float a = dot(p, uWind);
  ap = max(a, 0.0);
  vec3 q = p - a * uWind - uWindU * uBend * ap * ap;
  float x = dot(q, uWindU);
  float y = dot(q, uWindV);
  float r = length(vec2(x, y));
  // A sheath of gas peeling off the terminator ring, slowly converging, with a core that fills in
  // downwind. The planet's shadow darkens the sheath's inside, so it reads as two lit streamers.
  float R = max(1.06 - 0.025 * ap, 0.55);
  float sheath = exp(-pow((r - R) / (0.09 + 0.05 * ap), 2.0));
  float core = exp(-pow(r / R, 2.0) * 1.6) * smoothstep(0.5, 5.0, ap);
  float prof = sheath + core * 0.45;
  float onset = smoothstep(-0.15, 0.9, a);
  float fall = exp(-ap / uLength);
  float shape = prof * onset * fall;
  if (shape < 0.004) return 0.0;
  // Streaky: low frequency along the flow, high across it, carried downwind on the clock.
  float flow = a - uTime * uFlowSpeed;
  vec3 sp = vec3(flow * 0.045, x * 0.32, y * 0.32);
  float nz = n3(sp) * 0.65 + n3(sp * 2.7 + 0.37) * 0.35;
  float fil = pow(clamp(nz * 1.35 - 0.15, 0.0, 1.0), 2.2) * 2.2 + 0.08;
  // Gusts: broad pulses travelling down the tail.
  float gust = 0.7 + 0.6 * n3(vec3(flow * 0.012, 0.5, 0.5));
  return shape * fil * gust;
}

/** The ray's [enter, exit] through a sphere, clipped to [0, tMax]; empty when x >= y. */
vec2 sphereSpan(vec3 ro, vec3 rd, vec3 c, float r, float tMax) {
  vec3 oc = ro - c;
  float b = dot(oc, rd);
  float h = b * b - dot(oc, oc) + r * r;
  if (h <= 0.0) return vec2(1.0, 0.0);
  h = sqrt(h);
  return vec2(max(-b - h, 0.0), min(-b + h, tMax));
}

/** Emission of the plume at p: lit gas pale, gas in the planet's shadow a faint violet glow. */
vec3 gas(vec3 p, vec3 rd) {
  float ap;
  float d = plume(p, ap);
  if (d < 0.002) return vec3(0.0);
  float lit = sunlit(p);
  float fwd = pow(max(dot(rd, uLight), 0.0), 5.0);
  vec3 litCol = mix(uCoreColor, uTailColor, smoothstep(0.0, uLength, ap) * 0.6);
  return mix(uTailColor * 0.35, litCol, lit) * d * (0.8 + 1.4 * fwd);
}

void main() {
  vec2 ndc = vUv * 2.0 - 1.0;
  vec4 vp = uInvProj * vec4(ndc, 1.0, 1.0);
  vec3 rd = normalize(mat3(uCamWorld) * (vp.xyz / vp.w));
  vec3 ro = uCamPos;

  // Planet hit.
  float b = dot(ro, rd);
  float c = dot(ro, ro) - 1.0;
  float hdisc = b * b - c;
  float tHit = hdisc > 0.0 ? -b - sqrt(hdisc) : 1e9;

  vec3 col = vec3(0.0);

  // ── Exosphere at the limb (analytic column through an exponential shell) ─────────────────
  float tc = -b;
  vec3 pc = ro + rd * tc;
  float bImpact = length(pc);
  vec3 pAt = bImpact >= 1.0 ? pc : ro + rd * tHit;
  vec3 nAt = normalize(pAt);
  float cw = dot(nAt, uWind);
  // Scale height: pressed thin on the dayside, blown out on the night side; ragged on the clock.
  vec3 adv = nAt * 0.5 - uWind * uTime * uFlowSpeed * 0.05;
  float rag = n3(adv) * 0.55 + n3(adv * 3.3 + 0.21) * 0.45;
  float H = mix(0.010, 0.09, smoothstep(-0.8, 1.0, cw)) * (0.3 + 3.0 * rag * rag * rag);
  float column;
  if (bImpact >= 1.0) {
    column = sqrt(6.2832 * H) * exp(-(bImpact - 1.0) / H);
  } else {
    float mu = max(dot(nAt, -rd), 0.0);
    column = H / max(mu, sqrt(H / 6.2832));
  }
  float litAt = sunlit(pAt * 1.02);
  float dayFace = smoothstep(-0.35, 0.5, dot(nAt, uLight));
  float fwd = pow(max(dot(rd, uLight), 0.0), 5.0);
  col += uLimbColor * column * uLimb * (0.06 + 1.6 * dayFace * litAt + 1.2 * fwd * litAt);

  // ── Plume and tail (raymarched) ──────────────────────────────────────────────────────────
  // Two segments: fine steps through a near sphere where the sheath peels off the limb, coarse
  // steps through a far sphere around the tail's span (skipping the near sphere).
  vec2 nearT = sphereSpan(ro, rd, vec3(0.0), 2.6, tHit);
  vec3 cen = uWind * (uLength * 1.1) + uWindU * uBend * uLength * uLength;
  vec2 farT = sphereSpan(ro, rd, cen, uLength * 1.3 + 2.0, tHit);
  float jitter = hash(gl_FragCoord.xy);
  vec3 acc = vec3(0.0);
  if (nearT.y > nearT.x) {
    const int NEAR = 20;
    float dt = (nearT.y - nearT.x) / float(NEAR);
    for (int i = 0; i < NEAR; i++) acc += gas(ro + rd * (nearT.x + dt * (float(i) + jitter)), rd) * dt;
  }
  if (farT.y > farT.x) {
    // The far span minus the near one: up to two intervals, stepped as one concatenated length.
    vec2 a1 = vec2(farT.x, nearT.y > nearT.x ? min(farT.y, nearT.x) : farT.y);
    vec2 a2 = nearT.y > nearT.x ? vec2(max(farT.x, nearT.y), farT.y) : vec2(0.0);
    float len1 = max(a1.y - a1.x, 0.0);
    float len2 = max(a2.y - a2.x, 0.0);
    const int FAR = 34;
    float dt = (len1 + len2) / float(FAR);
    for (int i = 0; i < FAR; i++) {
      float s = dt * (float(i) + jitter);
      float t = s < len1 ? a1.x + s : a2.x + (s - len1);
      acc += gas(ro + rd * t, rd) * dt;
    }
  }
  col += acc * uTail;

  gl_FragColor = vec4(col, 1.0);
}
`;

/** Composite the low-resolution wind pass over the scene, with a soft shoulder instead of a clip. */
export const COMPOSITE_FRAGMENT = /* glsl */ `
uniform sampler2D uWindTex;
uniform vec2 uTexel;
varying vec2 vUv;
void main() {
  // A 3x3 tent over the low-resolution pass hides the march's jitter grain.
  vec3 c = vec3(0.0);
  for (int y = -1; y <= 1; y++)
    for (int x = -1; x <= 1; x++) {
      float w = (2.0 - abs(float(x))) * (2.0 - abs(float(y)));
      c += w * texture2D(uWindTex, vUv + uTexel * vec2(float(x), float(y))).rgb;
    }
  c /= 16.0;
  c = 1.0 - exp(-c * 1.1);
  gl_FragColor = vec4(c, 1.0);
}
`;

/** Streaks carried on the wind: each segment slides downwind along the tail's own bend. */
export const STREAK_VERTEX = /* glsl */ `
attribute vec2 aOffset;
attribute float aStart;
attribute float aSpan;
attribute float aSpeed;
attribute float aPhase;
attribute float aLen;
attribute float aEnd;
attribute float aGlow;
uniform vec3 uWind;
uniform vec3 uWindU;
uniform vec3 uWindV;
uniform vec3 uLight;
uniform float uTime;
uniform float uBend;
varying float vAlpha;
void main() {
  float s = fract(aPhase + uTime * aSpeed / aSpan);
  float a = aStart + s * aSpan - (1.0 - aEnd) * aLen;
  float ap = max(a, 0.0);
  vec2 off = aOffset * (1.0 + 0.16 * ap);
  vec3 p = uWind * a + uWindU * (off.x + uBend * ap * ap) + uWindV * off.y;
  float along = dot(p, uLight);
  float perp = length(p - along * uLight);
  float lit = mix(1.0, smoothstep(0.88, 1.12, perp), smoothstep(0.0, 0.5, -along));
  float fade = sin(3.14159 * s);
  vAlpha = aGlow * fade * fade * aEnd * (0.25 + 0.75 * lit) * step(1.0, length(p));
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}
`;

export const STREAK_FRAGMENT = /* glsl */ `
uniform vec3 uColor;
varying float vAlpha;
void main() {
  gl_FragColor = vec4(uColor * vAlpha, 1.0);
}
`;
